/**
 * FlashBook — publishing decisions.
 *
 * Kept apart from `flashbookSave.ts` on purpose: that module pulls in the whole
 * media pipeline (blob uploads, gallery, video processing), and the rules here —
 * which repo write publishing uses, what the post says about its book, and which
 * marks a book written for one classroom carries — are pure decisions that
 * deserve to be testable, and reasoned about, on their own.
 */
import {AtUri} from '@atproto/api'

import {CLASSROOM_ONLY_POST} from '#/state/classrooms/types'

/**
 * Which repo write publishing should use for the flashbook record.
 *
 * A book loaded from a draft already has a record, and publishing is meant to
 * *convert* it: same rkey, `status: 'draft'` → `'published'`, so the draft does
 * not survive publishing as an orphan record with no post pointing at it. A book
 * with no draft behind it has nothing to convert, so publishing creates one as
 * before.
 */
export type FlashBookPublishTarget =
  | {mode: 'update'; rkey: string}
  | {mode: 'create'}

export function flashbookPublishTarget(
  draftUri?: string | null,
): FlashBookPublishTarget {
  if (!draftUri) return {mode: 'create'}
  return {mode: 'update', rkey: new AtUri(draftUri).rkey}
}

/**
 * Who a book is published *for*.
 *
 * `public` is the ordinary publish: the post goes on the creator's profile, the
 * book hangs off it, and the feeds, the shelf and every classroom can reach it.
 * `classroom` writes the same records with one difference on each: the book says
 * `visibility: 'classroom'` and so does the post, which is how the app knows to
 * keep a room's own book out of profiles, feeds and grids while the room that
 * holds it reads it. It is the answer the capsule publisher gives in the same
 * words, because it is the same rule about the same two records.
 *
 * A post cannot be made private on AT Protocol, so the mark is not a lock — it is
 * the truth about who the book was written for, said on the record where it can
 * be read and, once classrooms live on a server, enforced.
 */
export type FlashBookAudience = 'public' | 'classroom'

/**
 * The marks a publish writes for its audience.
 *
 * The post's mark is the same string the capsule publisher writes
 * (`CLASSROOM_ONLY_POST`), which is the whole point: the app's own hiding rule
 * reads the post's record without caring what kind of work it carries, so a book
 * made for a room is kept out of every feed by the line of code that already
 * keeps a capsule out of them.
 *
 * Returned as data rather than applied inside the record builders so the two
 * writes cannot disagree: the publisher spreads these onto the post record it
 * creates, and onto the same record again when it patches in the book's
 * back-reference — one answer, read twice.
 */
export function flashbookAudience(forClassroom: boolean): {
  postVisibility?: string
  recordVisibility?: string
} {
  return forClassroom
    ? {postVisibility: CLASSROOM_ONLY_POST, recordVisibility: 'classroom'}
    : {}
}

/**
 * The post a published book is carried by.
 *
 * The first of publishing's two writes. It says `type: 'flashbook'`, which is what
 * makes the post recognisable as a book at all, and it carries the book's own words.
 *
 * `visibility` rides on *this* record rather than being added to the patch alone,
 * because publishing writes this record twice — created, then replaced with the
 * book's back-reference spread over it. Anything the second write had to remember
 * by itself is a field that can go missing from a book whose patch failed, which
 * for this field means a room's own book sitting in the public feed.
 */
export function flashbookPostRecord({
  text,
  createdAt,
  embed,
  visibility,
}: {
  text: string
  createdAt: string
  /** The cover film's own embed, when the cover is one. */
  embed?: Record<string, unknown>
  /** `CLASSROOM_ONLY_POST` when the book was written for one classroom. */
  visibility?: string
}) {
  return {
    $type: 'app.bsky.feed.post',
    text,
    type: 'flashbook',
    createdAt,
    embed,
    ...(visibility ? {visibility} : null),
  }
}

/**
 * What the post says about the book it holds.
 *
 * The second write: the same post again, carrying the back-ref that names the book
 * and the few facts a *feed card* can state before the book record has arrived. A
 * card is drawn in a scrolling timeline, and a row that is blank until a second
 * request lands reads as a broken post — so the numbers a reader wants anyway (how
 * long the book is, how much of it is work, which theme it was written in) ride on
 * the post that names it.
 *
 * The cover's **kind and picture** ride along for the same reason, and the picture
 * is the one that matters most: a cover that cannot be drawn is the one thing a card
 * cannot recover from looking like, and a still lives on the *record* otherwise —
 * one request later. Both are read off the same cover the record is written with, so
 * the two halves of a publish cannot disagree about which picture a book has.
 *
 * Read as one function by the publisher *and* by the test fixture that stands in for
 * it, because a fixture that restated these field names would be free to drift from
 * the publisher while every gate kept passing.
 */
export function flashbookPostFacts({
  bookUri,
  bookCid,
  pageCount,
  exerciseCount,
  themeId,
  cover,
}: {
  bookUri: string
  bookCid: string
  pageCount: number
  exerciseCount: number
  themeId: string
  cover?: {
    kind: 'image' | 'audio' | 'video'
    imageUri?: string
    videoThumbnailUri?: string
  }
}) {
  return {
    flashbook: {uri: bookUri, cid: bookCid},
    pageCount,
    exerciseCount,
    themeId,
    coverKind: cover?.kind,
    coverImageUri: cover?.imageUri ?? cover?.videoThumbnailUri,
  }
}
